import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'

import { useToast } from '@wartek-id/toast-v2'
import { Button } from '@wartek-id/button-v1'
import { Spinner } from '@wartek-id/spinner'
import { Icon } from '@wartek-id/icon-v2'

import { Header } from 'app/KinerjaShared/components/Header'
import { DatesSelector } from 'app/KinerjaHarian/components/forms/DatesSelector'
import { KinerjaLogForm } from 'app/KinerjaHarian/components/forms/KinerjaLogForm'
import { ConfirmationSection } from 'app/KinerjaHarian/components/forms/ConfirmationSection'
import { useAPIGetHolidayDates } from 'api/kinerja/holiday'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import {
  API_GET_KINERJA_HARIAN_LOGS_QUERY_KEY,
  useAPIGetKinerjaHarianContents,
  useAPIGetKinerjaHarianLogs,
  useAPIPostUpsertKinerjaHarian,
} from 'api/kinerja/kinerja-harian'
import DialogSavePostSendConfirmation from 'app/KinerjaHarian/components/forms/DialogSavePostSendConfirmation'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { MIN_SENT_TO_BKN_COUNT } from 'app/KinerjaHarian/constants'
import { useKinerjaHarianStore } from 'stores/kinerja-harian'
import { useKinerjaHarianRole } from 'app/KinerjaHarian/hooks/useKinerjaHarianRole'
import { useKinerjaHarianTrackEvent } from 'app/KinerjaHarian/hooks/useKinerjaHarianTrackEvent'

import type {
  KinerjaHarianFormProps,
  RepeatingDateType,
} from 'types/kinerja-harian'

type Props = {
  basePath: string
  entryDate?: string
}

export default function AddEditKinerjaHarianLogs({
  basePath,
  entryDate,
}: Props) {
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = useKinerjaHarianTrackEvent()

  const { surveyDone, setShowSurveyDialog } = useKinerjaHarianStore()
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: basePath,
  })
  const { role } = useKinerjaHarianRole()
  const { tipe } = useKinerjaTipe()
  const queryClient = useQueryClient()

  const [startDate, setStartDate] = useState<string | null>(entryDate || null)

  const { periodYear } = usePeriodContext()

  const {
    data: { data: kinerjaHarianContents = [] } = {},
    isLoading: isContentLoading,
    isSuccess: isContentSuccess,
  } = useAPIGetKinerjaHarianContents({
    params: {
      role,
      tipe,
      requestedDate: `${periodYear}-01-01`,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 60 minutes
      enabled: Boolean(role) && Boolean(tipe), // make sure query only runs when we have required params
    },
  })

  const editMode = !!entryDate

  const {
    data: logsToEdit,
    isSuccess: isLogsToEditFetched,
    isLoading: isLogsLoading,
  } = useAPIGetKinerjaHarianLogs({
    params: {
      startDate: entryDate || undefined,
      endDate: entryDate || undefined,
    },
    options: {
      enabled: editMode, // Only fetch logs if entryDate is provided (edit mode)
      onSuccess: (data) => {
        if (data.data.length === 0) {
          // Redirect to list page if no logs are found
          router.replace(basePath)
        }
        // if sentToBknCount >= MIN_SENT_TO_BKN_COUNT, it means the log has been processed or already sent to BKN
        // and cannot be edited
        else if (data.data[0]?.sentToBknCount >= MIN_SENT_TO_BKN_COUNT) {
          // Redirect to list page
          handleBack()
        }
      },
    },
  })

  const [repeatingDateType, setRepeatingDateType] =
    useState<RepeatingDateType>('non-repeating')

  const { data: holidayDates } = useAPIGetHolidayDates({
    params: {
      year: periodYear,
    },
  })

  const holidayDateSet = useMemo(
    // expected date format in API response is 'YYYY-MM-DD'
    () => new Set(holidayDates?.data?.holiday?.map((h) => h.date) ?? []),
    [holidayDates]
  )

  const [entryDates, setEntryDates] = useState<string[]>([])

  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const methods = useForm<KinerjaHarianFormProps>({
    mode: 'onBlur',
    defaultValues: {
      absent: false,
      contents: {},
    },
  })

  // Watch form values for validation
  const formValues = methods.watch()
  const { reset, formState } = methods
  const isAbsentSelected = editMode && !!formValues.absent

  // Validate form is ready for submission
  const isFormValid = useMemo(() => {
    if (entryDates.length === 0 || !formState.isValid) return false

    const checkedContents = Object.values(formValues.contents || {}).filter(
      (c) => c.isChecked
    )
    const areCheckedContentsValid = checkedContents.every(
      (c) => c.startTime && c.endTime
    )

    return (
      (isAbsentSelected || checkedContents.length > 0) &&
      areCheckedContentsValid
    )
  }, [entryDates, formValues, formState.isValid, isAbsentSelected])

  useEffect(() => {
    trackEvent({
      activityType: 'page_view',
      screenName: 'add',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pre-fill form with data from the first log entry when in edit mode
  useEffect(() => {
    if (isContentSuccess) {
      // Check if we're in edit mode with successfully fetched log data
      const hasLogData =
        isLogsToEditFetched && logsToEdit?.data && logsToEdit.data.length > 0

      const firstLog = hasLogData ? logsToEdit.data[0] : null

      reset({
        absent: !!firstLog?.absent,
        contents: kinerjaHarianContents.reduce((acc, option) => {
          // Find if this content exists in the log data
          const existingContent = firstLog?.contents.find(
            (c) => c.contentId === option.id
          )

          acc[option.id] = {
            isChecked: !!existingContent,
            startTime: existingContent?.startHour || '',
            endTime: existingContent?.endHour || '',
          }

          return acc
        }, {} as KinerjaHarianFormProps['contents']),
      })
    }
  }, [
    isContentSuccess,
    isLogsToEditFetched,
    logsToEdit,
    kinerjaHarianContents,
    reset,
  ])

  // API mutation for submitting kinerja harian
  const { mutate, isLoading: isSubmitting } = useAPIPostUpsertKinerjaHarian()

  const showConfirmationDialog =
    editMode && logsToEdit?.data[0]?.state === 'SENT_TO_BKN_SUCCESS'

  /**
   * Handle confirmed submission - construct payload and call API
   * This is called either directly or after dialog confirmation
   */
  const handleConfirmedSubmit = useCallback(() => {
    // Construct content array from checked items
    const contents = Object.entries(formValues.contents || {})
      .filter(([, value]) => value.isChecked)
      .map(([key, value]) => ({
        contentId: key,
        startHour: value.startTime,
        endHour: value.endTime,
      }))

    const payload = {
      entriesDate: entryDates,
      data: {
        contents,
        absent: editMode ? !!formValues.absent : false,
      },
      eventType: editMode ? ('EDIT' as const) : ('UPSERT' as const),
    }

    trackEvent({
      activityType: 'click_save',
      screenName: 'add',
      additionalParams: {
        edit_type: editMode ? 'edit' : 'create',
      },
    })

    mutate(payload, {
      onSuccess: () => {
        toast({
          message: 'Berhasil simpan catatan kinerja!',
          type: 'success',
        })

        // Invalidate Kinerja Harian logs query to refresh data on the list page
        queryClient.invalidateQueries(API_GET_KINERJA_HARIAN_LOGS_QUERY_KEY)

        setIsDialogOpen(false)
        // get month from the first entry date for redirect query param
        const month = entryDates[0]?.split('-')[1]

        if (!surveyDone) {
          setShowSurveyDialog(true)
        }

        router.replace({
          pathname: basePath,
          query: {
            month: month ? Number(month) : undefined,
          },
        })
      },
      onError: (error: any) => {
        toast({ message: 'Gagal simpan catatan kinerja. Coba lagi.' })

        // Log error for debugging
        console.error('Kinerja Harian submission error:', error)
        setIsDialogOpen(false)
      },
    })
  }, [
    formValues.contents,
    formValues.absent,
    entryDates,
    editMode,
    trackEvent,
    mutate,
    toast,
    queryClient,
    surveyDone,
    router,
    basePath,
    setShowSurveyDialog,
  ])

  /**
   * Handle form submission - show confirmation dialog if needed, otherwise submit directly
   */
  const handleSubmit = useCallback(() => {
    if (showConfirmationDialog) {
      setIsDialogOpen(true)
    } else {
      handleConfirmedSubmit()
    }
  }, [showConfirmationDialog, handleConfirmedSubmit])

  if (
    isContentLoading ||
    // in edit mode, wait for logs fetch so sentToBknCount is confirmed first
    (editMode && isLogsLoading) ||
    // hide the form before redirecting if no log is found or
    // the existing log has been processed or already sent to BKN
    (editMode &&
      (logsToEdit?.data?.length === 0 ||
        logsToEdit?.data[0].sentToBknCount >= MIN_SENT_TO_BKN_COUNT))
  ) {
    return (
      <div
        className="flex flex-col items-center justify-center h-screen gap-4 pt-9"
        aria-busy="true"
      >
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center px-2 py-10">
      <div className="w-full max-w-4xl mx-auto">
        <header>
          <Header title="Catat Kinerja Harian" onBack={handleBack} />
        </header>
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(handleSubmit)}
            className="flex flex-col gap-5 mt-4"
          >
            <DatesSelector
              startDate={startDate}
              onChangeStartDate={setStartDate}
              repeatingDateType={repeatingDateType}
              onChangeRepeatingDateType={setRepeatingDateType}
              onChangeEntryDates={setEntryDates}
              holidayDates={holidayDateSet}
              disabled={editMode} // Disable date selection in edit mode
              minValue={`${periodYear}-01-01`}
              maxValue={`${periodYear}-12-31`}
              minYear={periodYear}
              maxYear={periodYear}
            />

            <KinerjaLogForm
              contents={kinerjaHarianContents}
              editMode={editMode}
            />

            <ConfirmationSection
              confirmationText={
                isAbsentSelected
                  ? 'Saya menyatakan tidak hadir pada tanggal di atas'
                  : 'Saya melaksanakan Kinerja Harian sesuai dengan data di atas'
              }
              isConfirmed={isConfirmed}
              onConfirmChange={setIsConfirmed}
            />

            <DialogSavePostSendConfirmation
              isOpen={isDialogOpen}
              isLoading={isSubmitting}
              onClose={() => setIsDialogOpen(false)}
              onConfirm={handleConfirmedSubmit}
            />

            <Button
              type="submit"
              variant="solid"
              color="blue"
              disabled={!isConfirmed || !isFormValid || isSubmitting}
              isLoading={isSubmitting}
              fullWidth
            >
              {editMode && (
                <Icon className="mr-1" color="inherit">
                  edit_outline
                </Icon>
              )}
              Simpan
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
